import { Dialog as DialogPrimitive } from 'radix-ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { showDate } from '../../../shared/tree'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { albumPhotoUrl, photoName, photoSrcSet, type AlbumPhoto } from '@/lib/albums'

// A photo in the storybook, at the size the browser picks for where it's shown.
export function StoryImg({
  photo,
  sizes,
  className,
  eager = false,
  alt,
}: {
  photo: AlbumPhoto
  sizes: string
  className?: string
  eager?: boolean
  alt?: string
}) {
  return (
    <img
      src={albumPhotoUrl(photo.ref, 'medium')}
      srcSet={photoSrcSet(photo, ['thumb', 'medium', 'display'])}
      sizes={sizes}
      width={photo.width}
      height={photo.height}
      alt={alt ?? photoName(photo, 0)}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
      className={className}
    />
  )
}

// A photo picked up out of the story: held close, and turned over to read its back the way a print is, with when it
// was taken, the caption and who's in it. The original, exactly as it was added, is one more tap.
// `from` says where the print came from: whose album, or who added it.
export function PickedUp({ photo, from, onClose }: { photo: AlbumPhoto | null; from: string; onClose: () => void }) {
  const [turned, setTurned] = useState(false)
  return (
    <DialogPrimitive.Root
      open={!!photo}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          setTurned(false)
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[55] bg-viewer/80 backdrop-blur-sm duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        {photo && (
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-0 z-[56] flex flex-col items-center justify-center gap-5 px-5 pt-[calc(var(--safe-top)+1.5rem)] pb-[calc(var(--safe-bottom)+1rem)] text-viewer-foreground outline-none"
            onPointerDown={(e) => e.target === e.currentTarget && onClose()}
          >
            <DialogPrimitive.Title className="sr-only">{photoName(photo, 0)}</DialogPrimitive.Title>
            <p className="rounded-full bg-viewer/60 px-3 py-1.5 text-xs font-semibold">Storybook version</p>
            <div className="sb-card w-full max-w-md" data-turned={turned}>
              <div className="sb-card-inner">
                <button
                  type="button"
                  inert={turned}
                  className="sb-card-front sb-print block w-full cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
                  aria-label="Turn it over"
                  onClick={() => setTurned(true)}
                >
                  <StoryImg photo={photo} sizes="(min-width: 28rem) 28rem, 100vw" eager />
                </button>
                <div className="sb-card-back" inert={!turned} data-testid="photo-back">
                  <p className="eyebrow text-sheet-muted">{photo.takenOn ? showDate(photo.takenOn) : 'No date yet'}</p>
                  {photo.caption ? (
                    <p className="text-lg leading-snug font-medium text-pretty">{photo.caption}</p>
                  ) : (
                    <p className="text-sm text-sheet-muted">No caption yet</p>
                  )}
                  {photo.people.length > 0 ? (
                    <ul className="flex flex-wrap gap-2" aria-label="Who’s in it">
                      {photo.people.map((person) => (
                        <li key={person.id}>
                          <Link
                            to={`/person/${person.id}`}
                            className="flex items-center gap-2 rounded-full bg-sheet py-1 pr-3 pl-1 text-sm shadow-paper outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
                          >
                            <Avatar person={person} className="size-7 text-xs" />
                            {person.name}
                            {person.living === false && <span className="text-sheet-muted">in memory</span>}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-sheet-muted">No one tagged yet</p>
                  )}
                  {from && <p className="mt-auto text-xs text-sheet-muted">{from}</p>}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="secondary" onClick={() => setTurned((x) => !x)}>
                {turned ? 'Turn it back' : 'Turn it over'}
              </Button>
              <OriginalPhoto
                src={albumPhotoUrl(photo.id, 'original')}
                fallback={albumPhotoUrl(photo.ref, 'display')}
                alt={photoName(photo, 0)}
              >
                <Button
                  variant="outline"
                  className="border-viewer-foreground/30 bg-transparent text-viewer-foreground hover:bg-viewer-foreground/10 hover:text-viewer-foreground"
                >
                  See the original
                </Button>
              </OriginalPhoto>
              <DialogPrimitive.Close asChild>
                <Button
                  variant="outline"
                  className="border-viewer-foreground/30 bg-transparent text-viewer-foreground hover:bg-viewer-foreground/10 hover:text-viewer-foreground"
                >
                  Put it back
                </Button>
              </DialogPrimitive.Close>
            </div>
          </DialogPrimitive.Content>
        )}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
